import type { CompiledChart } from '../songpack/index.js';
import { ChartCursor } from './chart/ChartCursor.js';
import {
  DEFAULT_PLAYER_POSITION,
  SpawnResolver,
  type SpawnResolverConfig,
  type Vector2Like,
} from './chart/SpawnResolver.js';
import {
  DEFAULT_BEST_TIME_SETTINGS,
  resolveBestTime,
  type BestTimeSettings,
} from './judge/BestTime.js';
import {
  DEFAULT_JUDGE_SETTINGS,
  breaksCombo,
  clearsWave,
  directionForOffset,
  fadeForGrade,
  gradeForOffset,
  scoreForGrade,
  type JudgeSettings,
  type TimingDirection,
  type TimingGrade,
  type WaveAttempt,
  type WaveJudgement,
} from './judge/Judgement.js';
import {
  SimulationEventQueue,
  type SimulationEvent,
  type WaveResolvedEvent,
} from './SimulationEvents.js';
import type { SimulationState } from './SimulationState.js';
import type { Wave } from './wave/Wave.js';
import { WaveState, isWaveVisible } from './wave/WaveState.js';
import {
  DEFAULT_VISUAL_LEAD,
  DEFAULT_WAVE_SYSTEM_CONFIG,
  WaveSystem,
  applyWaveSpeed,
  visualLeadFor,
  type VisualLeadSettings,
  type WaveSystemConfig,
} from './wave/WaveSystem.js';

/**
 * The simulation: a chart, a clock reading, and the waves that result.
 *
 * Imports nothing from `three`, the DOM or Web Audio. It is advanced by being
 * told what time it is — `advanceTo(songTime)` — and that time comes from
 * `MusicClock`, which reads `AudioContext.currentTime`. There is no internal
 * clock to drift, and no delta to accumulate.
 *
 * It now also judges. A tap arrives as a world point and a song time, is matched
 * to a wave, graded against that wave`s best moment and booked into the score —
 * all without the layer learning what a pointer, a camera or a canvas is. There
 * is still no player avatar and no collision: under the current rule the only
 * thing that can happen to a wave is being tapped, or not being tapped.
 */

export interface SimulationConfig extends Partial<WaveSystemConfig> {
  readonly fieldRadius?: number;
  readonly playerPosition?: Vector2Like;
  /**
   * Song length. Past this the scheduler stops consuming, matching Unity's
   * `songTime > sessionDuration` guard. Zero means "no limit".
   */
  readonly sessionDuration?: number;
  readonly judge?: Partial<JudgeSettings>;
  readonly bestTime?: Partial<BestTimeSettings>;
  /**
   * The player's wave speed, as a multiplier on how fast seals appear and grow.
   *
   * Presentation only. It reshapes `visualSpawnTime`, `visualExpansionStart`
   * and `visualSpeed` around `bestTime`, and touches nothing else — not the
   * chart, not the clock, not a single judging threshold.
   */
  readonly waveSpeedMultiplier?: number;
  /** How long a wave is shown before its best moment. Presentation only. */
  readonly visualLead?: Partial<VisualLeadSettings>;
}

/**
 * Extra seconds of scheduling headroom beyond the display lead itself.
 *
 * A wave now becomes visible a fixed time before its best moment, and on this
 * pack that moment is about a second *after* the chart event — so at every
 * speed the wave has to appear before its own event and the cursor has to run
 * ahead. Consuming a full lead early covers that with room to spare; the extra
 * waves simply wait in `Pending`, invisible and untappable.
 */
const SPAWN_LOOKAHEAD_MARGIN = 0.25;

/** Keeps a user-supplied multiplier inside a range the scheduler can serve. */
function clampWaveSpeed(multiplier: number): number {
  return Number.isFinite(multiplier) ? Math.min(4, Math.max(0.25, multiplier)) : 1;
}


export class Simulation {
  readonly state: SimulationState;

  private readonly cursor: ChartCursor;
  private readonly resolver: SpawnResolver;
  private readonly waves: WaveSystem;
  private readonly queue = new SimulationEventQueue();
  private readonly sessionDuration: number;
  readonly judgeSettings: JudgeSettings;
  private readonly bestTimeSettings: BestTimeSettings;
  private waveSpeedMultiplier: number;
  private spawnLookahead = 0;
  private readonly visualLeadSettings: VisualLeadSettings;
  /** Reused across spawns so resolving a position allocates nothing. */
  private readonly origin = { x: 0, y: 0 };
  private announcedExhausted = false;

  constructor(
    readonly chart: CompiledChart,
    config: SimulationConfig = {},
  ) {
    const resolverConfig: SpawnResolverConfig = {
      fieldRadius: config.fieldRadius ?? 9,
      playerPosition: config.playerPosition ?? DEFAULT_PLAYER_POSITION,
    };
    const waveConfig: WaveSystemConfig = {
      despawnMargin: config.despawnMargin ?? DEFAULT_WAVE_SYSTEM_CONFIG.despawnMargin,
      maxSimultaneousWaves:
        config.maxSimultaneousWaves ?? DEFAULT_WAVE_SYSTEM_CONFIG.maxSimultaneousWaves,
      capacity: config.capacity ?? DEFAULT_WAVE_SYSTEM_CONFIG.capacity,
      resolveFadeSeconds:
        config.judge?.resolveFadeSeconds ??
        config.resolveFadeSeconds ??
        DEFAULT_WAVE_SYSTEM_CONFIG.resolveFadeSeconds,
    };
    this.judgeSettings = { ...DEFAULT_JUDGE_SETTINGS, ...config.judge };
    this.bestTimeSettings = { ...DEFAULT_BEST_TIME_SETTINGS, ...config.bestTime };
    // Set directly rather than through `setWaveSpeed`, which walks the pool —
    // and the pool does not exist yet at this point in construction.
    this.visualLeadSettings = { ...DEFAULT_VISUAL_LEAD, ...config.visualLead };
    this.waveSpeedMultiplier = clampWaveSpeed(config.waveSpeedMultiplier ?? 1);
    this.spawnLookahead =
      visualLeadFor(this.waveSpeedMultiplier, this.visualLeadSettings) + SPAWN_LOOKAHEAD_MARGIN;

    this.cursor = new ChartCursor(chart);
    this.resolver = new SpawnResolver(chart, resolverConfig);
    this.waves = new WaveSystem(waveConfig);
    this.sessionDuration = config.sessionDuration ?? 0;

    this.state = {
      songTime: 0,
      waves: this.waves.pool,
      activeWaves: 0,
      consumedEvents: 0,
      totalEvents: chart.meta.eventCount,
      spawnedWaves: 0,
      retiredWaves: 0,
      droppedWaves: 0,
      peakSimultaneousWaves: 0,
      chartExhausted: chart.meta.eventCount === 0,
      pendingWaves: 0,
      score: 0,
      combo: 0,
      maxCombo: 0,
      judgeCounts: { perfect: 0, great: 0, good: 0, bad: 0, miss: 0 },
      earlyCount: 0,
      lateCount: 0,
      tappedJudgements: 0,
      timingOffsetSumMs: 0,
      lastJudgement: null,
      badAttempts: 0,
      lastAttempt: null,
      notYetVisibleWaves: 0,
      pooledWaves: 0,
    };
  }

  /**
   * Advances the world to `songTime`.
   *
   * Safe to call with any forward jump. Every event in the interval is consumed
   * on this call and every wave is re-derived from the new time, so a stalled
   * tab resumes correct rather than merely close.
   */
  advanceTo(songTime: number): void {
    const time = Math.max(0, songTime);
    this.state.songTime = time;

    // Unity stops feeding the scheduler past the session length; without this a
    // chart whose final events sit beyond the audio would keep spawning into
    // silence.
    const withinSession = this.sessionDuration <= 0 || time <= this.sessionDuration;
    if (withinSession) {
      // Below 1× the scheduler runs ahead so a wave can be on screen before its
      // own event time. Those waves spawn straight into `Pending` and stay
      // invisible and untappable until their visual spawn time — the chart is
      // read early, but nothing about the game happens early.
      this.cursor.consumeUntil(time + this.spawnLookahead, (eventIndex, chartTime) => {
        this.resolver.resolve(eventIndex, this.origin);
        const wave = this.waves.spawn(
          this.chart,
          eventIndex,
          this.origin.x,
          this.origin.y,
          time,
        );
        if (wave === null) {
          this.queue.push({ type: 'waveDropped', eventIndex, chartTime, songTime: time });
          return;
        }
        // Computed once, at spawn, from the chart alone. Recomputing it per
        // frame would be the same answer at a cost, and computing it per tap
        // would make the target depend on when you pressed.
        wave.bestTime = resolveBestTime(
          this.chart,
          wave.expansionStartTime,
          this.bestTimeSettings,
        );
        // Only now can the visual timeline be built, because every part of it
        // is measured backwards from the best moment.
        applyWaveSpeed(wave, this.waveSpeedMultiplier, time, this.visualLeadSettings);
        this.queue.push({
          type: 'waveSpawned',
          waveId: wave.id,
          eventIndex,
          chartTime,
          songTime: time,
        });
      });
    }

    this.waves.update(
      time,
      // Never tapped, and now past the play area. That is a MISS, and it is
      // deliberately a different fact from a badly timed tap: the counts have
      // to be able to tell "pressed at the wrong moment" from "never pressed".
      (wave) => this.judge(wave, time, false),
      (wave) => {
        this.queue.push({
          type: 'waveRetired',
          waveId: wave.id,
          eventIndex: wave.eventIndex,
          songTime: time,
          reason: wave.judgement === null ? 'expired' : 'resolved',
        });
      },
    );

    this.state.activeWaves = this.waves.activeCount;
    this.state.pooledWaves = this.waves.pooledCount;
    this.state.pendingWaves = this.waves.pendingCount;
    this.state.notYetVisibleWaves = this.waves.notYetVisibleCount;
    this.state.consumedEvents = this.cursor.consumed;
    this.state.spawnedWaves = this.waves.spawnedCount;
    this.state.retiredWaves = this.waves.retiredCount;
    this.state.droppedWaves = this.waves.droppedCount;
    this.state.peakSimultaneousWaves = this.waves.peakSimultaneous;

    if (this.cursor.exhausted && !this.announcedExhausted) {
      this.announcedExhausted = true;
      this.state.chartExhausted = true;
      this.queue.push({ type: 'chartExhausted', songTime: time });
    }
  }

  /**
   * The wave a tap at this world point should resolve, or null for a miss of
   * the input rather than of the beat.
   *
   * Only the disc of radius `noteHitRadius` around each wave's origin counts.
   * The inscribed rings, the timing arc and every other thing the seal draws
   * are not input surfaces — they are readouts, and a player who has learned to
   * press the centre must not find that a wave's decoration moved the target.
   *
   * ## Choosing between overlapping waves
   *
   * At the hard chart's peak, twenty-six seals share the field and their centre
   * discs overlap constantly, so this has to pick exactly one, the same way
   * every time. The order is:
   *
   * 1. **Nearest `bestTime` to now.** The player is aiming at a *moment*, not at
   *    a pixel — that is the whole premise of the rule — so the wave whose
   *    moment is closest to the press is overwhelmingly the one they meant.
   * 2. **Newest spawn.** A tie means two waves want the same instant; the one
   *    that arrived later is drawn later and reads as being in front.
   * 3. **Lowest `eventIndex`.** Chart order, so the outcome can never depend on
   *    which pool slot a wave happened to land in.
   *
   * Nothing here reads a camera, a screen size or a draw order, so the same tap
   * resolves the same wave under any resolution or camera move.
   */
  hitTest(worldX: number, worldY: number, songTime: number): Wave | null {
    const reach = this.judgeSettings.noteHitRadius;
    const reachSquared = reach * reach;
    let best: Wave | null = null;
    let bestDistance = Number.POSITIVE_INFINITY;

    for (const wave of this.state.waves) {
      if (wave.judgement !== null) continue;
      // Exactly the waves the renderer draws — one shared answer, so a wave can
      // never be tappable while invisible or visible while untappable.
      if (!isWaveVisible(wave)) continue;
      // Still cooling down from its own failed tap. Skipped rather than left to
      // swallow the input, so a neighbouring wave stays reachable — the lock
      // belongs to the wave, not to the player's finger.
      if (songTime < wave.lockedUntil) continue;
      const dx = worldX - wave.originX;
      const dy = worldY - wave.originY;
      if (dx * dx + dy * dy > reachSquared) continue;

      const distance = Math.abs(songTime - wave.bestTime);
      if (best === null) {
        best = wave;
        bestDistance = distance;
        continue;
      }
      if (distance < bestDistance - 1e-9) {
        best = wave;
        bestDistance = distance;
      } else if (distance <= bestDistance + 1e-9) {
        if (wave.spawnTime > best.spawnTime) {
          best = wave;
          bestDistance = distance;
        } else if (wave.spawnTime === best.spawnTime && wave.eventIndex < best.eventIndex) {
          best = wave;
          bestDistance = distance;
        }
      }
    }
    return best;
  }

  /**
   * Resolves a tap at a world point.
   *
   * Returns null only when the point was not inside any wave's centre — never
   * because of *when* the tap arrived. There is no early or late that refuses
   * the input; if a wave is on the field, tapping its centre kills it.
   */
  tap(worldX: number, worldY: number, songTime: number): WaveResolvedEvent | null {
    const wave = this.hitTest(worldX, worldY, songTime);
    if (wave === null) return null;
    return this.judge(wave, songTime, true);
  }

  /**
   * Resolves whichever unjudged wave is closest to its best moment.
   *
   * The seam for a key press: a keyboard has no position, so it can only mean
   * "the one that is due". Wired to nothing yet — the pointer is the only input
   * this phase binds — but the judging path below is shared with `tap`, so a
   * key will grade identically to a click when it is added.
   */
  tapNearestDue(songTime: number): WaveResolvedEvent | null {
    let best: Wave | null = null;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (const wave of this.state.waves) {
      if (!wave.active || wave.judgement !== null) continue;
      const distance = Math.abs(songTime - wave.bestTime);
      if (
        distance < bestDistance - 1e-9 ||
        (distance <= bestDistance + 1e-9 && best !== null && wave.eventIndex < best.eventIndex)
      ) {
        best = wave;
        bestDistance = distance;
      }
    }
    if (best === null) return null;
    return this.judge(best, songTime, true);
  }

  /**
   * Grades a wave and books the result.
   *
   * Returns null when nothing was resolved — either the wave was already
   * judged, or the tap was too far off to clear it. That second case is the
   * rule this phase introduced: a `bad` tap is a *failed attempt*, not a
   * resolution. The wave keeps standing, the combo goes, and the player is
   * free to take the same wave again once its cooldown expires.
   */
  private judge(wave: Wave, songTime: number, tapped: boolean): WaveResolvedEvent | null {
    if (!wave.active || wave.judgement !== null) return null;
    if (tapped && wave.state === WaveState.Pending) return null;
    if (tapped && songTime < wave.lockedUntil) return null;

    const offsetSeconds = songTime - wave.bestTime;
    const grade: TimingGrade = tapped ? gradeForOffset(offsetSeconds, this.judgeSettings) : 'miss';
    const direction: TimingDirection = tapped
      ? directionForOffset(offsetSeconds, this.judgeSettings)
      : 'late';

    if (tapped && !clearsWave(grade, this.judgeSettings)) {
      wave.badAttempts += 1;
      wave.lastBadAt = songTime;
      wave.lockedUntil = songTime + this.judgeSettings.badCooldownSeconds;
      this.state.combo = 0;
      this.state.badAttempts += 1;
      this.state.judgeCounts.bad += 1;
      if (direction === 'early') this.state.earlyCount += 1;
      else if (direction === 'late') this.state.lateCount += 1;
      // A failed tap is still a tap, and its offset is still the player's
      // timing. Leaving it out would flatter the average by discarding exactly
      // the presses that were furthest off.
      this.state.tappedJudgements += 1;
      this.state.timingOffsetSumMs += offsetSeconds * 1000;
      const attempt: WaveAttempt = {
        grade: 'bad',
        timingOffsetMs: offsetSeconds * 1000,
        timingDirection: direction,
        attempt: wave.badAttempts,
      };
      this.state.lastAttempt = attempt;
      this.queue.push({
        type: 'waveAttempt',
        waveId: wave.id,
        eventIndex: wave.eventIndex,
        songTime,
        bestTime: wave.bestTime,
        ...attempt,
      });
      return null;
    }

    const scoreAwarded = scoreForGrade(grade, this.judgeSettings);

    if (breaksCombo(grade, this.judgeSettings)) this.state.combo = 0;
    else this.state.combo += 1;
    if (this.state.combo > this.state.maxCombo) this.state.maxCombo = this.state.combo;
    this.state.score += scoreAwarded;
    this.state.judgeCounts[grade] += 1;
    if (tapped) {
      if (direction === 'early') this.state.earlyCount += 1;
      else if (direction === 'late') this.state.lateCount += 1;
      this.state.tappedJudgements += 1;
      this.state.timingOffsetSumMs += offsetSeconds * 1000;
    }

    const judgement: WaveJudgement = {
      grade,
      timingOffsetMs: offsetSeconds * 1000,
      timingDirection: direction,
      scoreAwarded,
      combo: this.state.combo,
      tapped,
    };
    if (!this.waves.markResolved(wave, judgement, songTime, fadeForGrade(grade, this.judgeSettings)))
      return null;
    this.state.lastJudgement = judgement;
    this.state.pendingWaves = this.waves.pendingCount;

    const resolved: WaveResolvedEvent = {
      type: 'waveResolved',
      waveId: wave.id,
      eventIndex: wave.eventIndex,
      grade,
      timingOffsetMs: judgement.timingOffsetMs,
      timingDirection: direction,
      scoreAwarded,
      combo: this.state.combo,
      tapped,
      songTime,
      bestTime: wave.bestTime,
      originX: wave.originX,
      originY: wave.originY,
      radius: wave.radius,
      badAttempts: wave.badAttempts,
      color: [wave.dangerColor[0] ?? 1, wave.dangerColor[1] ?? 1, wave.dangerColor[2] ?? 1],
    };
    this.queue.push(resolved);
    return resolved;
  }

  /**
   * The waves on screen right now.
   *
   * Exposed so that anything inspecting the field — diagnostics, a browser
   * probe, a test — selects waves the same way the renderer and the hit test do,
   * instead of writing its own predicate. One such hand-written predicate
   * (`state !== 0`) silently selected `Pending` waves, which are never drawn,
   * and produced a confident diagnosis of a bug that was somewhere else
   * entirely.
   */
  get visibleWaves(): readonly Wave[] {
    return this.waves.visibleWaves;
  }

  get waveSpeed(): number {
    return this.waveSpeedMultiplier;
  }

  /**
   * Sets the player's wave speed.
   *
   * Rebuilds the visual timeline of every live wave so the setting can be
   * changed without restarting, and nothing else: `bestTime` is not recomputed
   * because it does not depend on this, which is exactly the property a test
   * pins.
   */
  setWaveSpeed(multiplier: number): void {
    const clamped = clampWaveSpeed(multiplier);
    this.waveSpeedMultiplier = clamped;
    this.spawnLookahead =
      visualLeadFor(clamped, this.visualLeadSettings) + SPAWN_LOOKAHEAD_MARGIN;
    for (const wave of this.state.waves) {
      if (wave.active) {
        applyWaveSpeed(wave, clamped, this.state.songTime, this.visualLeadSettings);
      }
    }
  }

  /** Seconds a wave is shown before its best moment, at the current speed. */
  get visualLeadTime(): number {
    return visualLeadFor(this.waveSpeedMultiplier, this.visualLeadSettings);
  }

  /** Takes everything the simulation has reported since the last call. */
  drainEvents(): SimulationEvent[] {
    return this.queue.drain();
  }

  /** Time of the next event still to be consumed, or null when none remain. */
  nextEventTime(): number | null {
    return this.cursor.peekTime();
  }

  /** Returns the world to its starting state, ready to play the chart again. */
  reset(): void {
    this.cursor.reset();
    this.resolver.reset();
    this.waves.resetCounters();
    this.queue.clear();
    this.announcedExhausted = false;
    this.state.songTime = 0;
    this.state.activeWaves = 0;
    this.state.consumedEvents = 0;
    this.state.spawnedWaves = 0;
    this.state.retiredWaves = 0;
    this.state.droppedWaves = 0;
    this.state.peakSimultaneousWaves = 0;
    this.state.chartExhausted = this.chart.meta.eventCount === 0;
    // Scoring is part of the run, not part of the chart, so it resets with it.
    this.state.pendingWaves = 0;
    this.state.score = 0;
    this.state.combo = 0;
    this.state.maxCombo = 0;
    for (const grade of Object.keys(this.state.judgeCounts) as TimingGrade[]) {
      this.state.judgeCounts[grade] = 0;
    }
    this.state.earlyCount = 0;
    this.state.lateCount = 0;
    this.state.tappedJudgements = 0;
    this.state.timingOffsetSumMs = 0;
    this.state.lastJudgement = null;
    this.state.badAttempts = 0;
    this.state.lastAttempt = null;
    this.state.notYetVisibleWaves = 0;
    this.state.pooledWaves = 0;
  }
}
